/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { useFreshAgendaWorkshops } from '../../agenda/store/agenda.hooks';
import { useConfig } from '../../config/config.context';
import feedbackService from '../../core/services/feedback.service';
import { useMe } from '../../profile/hooks';
import FeedbackModal from '../cms/WorkshopFeedbackPopupBlock/FeedbackModal';

type FeedbackState = {
  uri?: string;
  feedbackId?: string;
  session?: any;
};

const FeedbackModalChecker = (): JSX.Element | null => {
  // Check if we have sessions with feedback
  const me = useMe();
  const [dismissedUri, setDismissedUri] = useState<string | undefined>();
  const agendaSessions = useFreshAgendaWorkshops();
  const sessionsWithFeedback = agendaSessions.filter((session: any) =>
    session?.feedbacks?.find((f: any) => f.visible && f.uri),
  );

  const hasFeedbacks = sessionsWithFeedback.length > 0;

  useEffect(() => {
    if (!hasFeedbacks) {
      setDismissedUri(undefined);
    }
  }, [hasFeedbacks]);

  // Check for feedback every 5 minutes
  const { data: feedbackData } = useQuery(
    ['feedback', 'live'],
    () => feedbackService.getMissingFeedbacks(me._id),
    {
      enabled: hasFeedbacks && !!me._id,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchInterval: 5 * 60 * 1000, // 5 minutes
    },
  );

  const feedbackState = useMemo((): FeedbackState => {
    if (!hasFeedbacks) {
      return {};
    }

    const display = feedbackData?.feedbackToDisplay;
    if (!display) {
      return {};
    }

    const { feedbackId, session, feedbackUrl } = display;
    if (dismissedUri === feedbackUrl) {
      return {};
    }

    return {
      uri: feedbackUrl,
      feedbackId,
      session,
    };
  }, [hasFeedbacks, feedbackData, dismissedUri]);

  if (!feedbackState.uri) {
    return null;
  }

  return (
    <FeedbackModal
      item={feedbackState.session}
      onClose={() => setDismissedUri(feedbackState.uri)}
      feedbackUrl={feedbackState.uri}
      autoClose
    />
  );
};

/**
 * Auto-disabled if doesn't have anything useful
 * @returns
 */
const FeedbackChecker = React.memo((): JSX.Element | null => {
  const config = useConfig();
  if (!config?.liveFeedback?.enabled) {
    return null;
  }

  return <FeedbackModalChecker />;
});

export default FeedbackChecker;
